import { getDownloadURL, ref, uploadBytes } from '../lib/offlineStorage';
import { doc, setDoc, serverTimestamp } from '../lib/offlineFirestore';
import { db, storage } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';
import offlineSyncService from './offlineSyncService';

const DB_NAME = 'coldstorage-company-branding-assets-v1';
const DB_VERSION = 1;
const STORE = 'assets';
const TENANTS = COLLECTIONS?.TENANTS || 'tenants';
const PUBLIC = COLLECTIONS?.TENANT_PUBLIC_PROFILES || 'tenantPublicProfiles';

const openDb = () => new Promise((resolve, reject) => {
  if (typeof window === 'undefined' || !window.indexedDB) return resolve(null);
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE)) {
      const store = database.createObjectStore(STORE, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('tenantId', 'tenantId', { unique: false });
    }
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const put = async (row) => {
  const database = await openDb();
  if (!database) throw new Error('IndexedDB is unavailable; branding upload cannot be queued offline.');
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(row);
    tx.oncomplete = () => { database.close(); resolve(row); };
    tx.onerror = () => { database.close(); reject(tx.error); };
  });
};

const remove = async (id) => {
  const database = await openDb().catch(() => null);
  if (!database) return;
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => { database.close(); resolve(); };
    tx.onerror = () => { database.close(); reject(tx.error); };
  });
};

const list = async () => {
  const database = await openDb().catch(() => null);
  if (!database) return [];
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => database.close();
  });
};

const safeField = (field = '') => String(field || '').replace(/[^a-zA-Z0-9_]/g, '');
const extensionOf = (file) => {
  const name = String(file?.name || 'asset.png');
  const ext = name.includes('.') ? name.split('.').pop() : 'png';
  return String(ext || 'png').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || 'png';
};

const patchBrandingUrl = async ({ tenantId, field, url }) => {
  const key = safeField(field);
  if (!tenantId || !key || !url) return;
  const patch = {
    branding: { [key]: url },
    updatedAt: serverTimestamp(),
  };
  await Promise.all([
    setDoc(doc(db, TENANTS, tenantId), patch, { merge: true }),
    setDoc(doc(db, PUBLIC, tenantId), patch, { merge: true }),
  ]);
};

const uploadRow = async (row) => {
  const target = ref(storage, row.storagePath);
  const snapshot = await uploadBytes(target, row.file, { contentType: row.contentType || undefined });
  const url = await getDownloadURL(snapshot.ref);
  await patchBrandingUrl({ tenantId: row.tenantId, field: row.field, url });
  return url;
};

const companyBrandingAssetService = {
  async uploadOrQueue({ tenantId, field, file }) {
    const id = String(tenantId || '').trim();
    const key = safeField(field);
    if (!id || !key || !file) throw new Error('Company, branding field and file are required.');
    if (!String(file.type || '').startsWith('image/')) throw new Error('Branding asset must be an image file.');
    if (Number(file.size || 0) > 8 * 1024 * 1024) throw new Error('Branding image must be smaller than 8 MB.');

    const row = {
      id: `BRAND-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      tenantId: id,
      field: key,
      file,
      fileName: file.name || `${key}.png`,
      contentType: file.type || 'image/png',
      storagePath: `tenants/${id}/branding/${key}-${Date.now()}.${extensionOf(file)}`,
      status: 'pending',
      createdAt: new Date().toISOString(),
      error: '',
    };

    if (offlineSyncService.isOfflineLikely()) {
      await put(row);
      return { pending: true, queueId: row.id, url: '' };
    }

    try {
      const url = await uploadRow(row);
      return { pending: false, queueId: '', url };
    } catch (error) {
      // If connectivity dies during upload, retain the actual File in IndexedDB
      // and let automatic sync finish it later.
      await put({ ...row, status: 'pending', error: String(error?.message || error || '') });
      return { pending: true, queueId: row.id, url: '' };
    }
  },

  async countPending() {
    const rows = await list();
    return rows.filter((row) => row.status !== 'synced').length;
  },

  async processQueue() {
    if (offlineSyncService.isOfflineLikely()) return { uploaded: 0, failed: 0 };
    const rows = await list();
    let uploaded = 0;
    let failed = 0;
    for (const row of rows) {
      try {
        await put({ ...row, status: 'uploading', error: '' });
        await uploadRow(row);
        await remove(row.id);
        uploaded += 1;
      } catch (error) {
        failed += 1;
        await put({ ...row, status: 'failed', error: String(error?.message || error || 'Upload failed'), updatedAt: new Date().toISOString() }).catch(() => {});
      }
    }
    return { uploaded, failed };
  },
};

export default companyBrandingAssetService;
